import { useCallback, useEffect, useRef, useState } from "react";
import { zim } from "../../../../zegoConfig";
import axios from "../../../../api/axios";

import { MediaKind, ParsedMessage, ReceivedMessage, ReplyToData } from "../types";
import {
  buildParsedMessage,
  parseBackendChatMessage,
  saveMessagesToLocalStorage,
  resolveMediaKind,
  safeJsonParse,
  formatTimestamp,
  parseReplyData,
} from "../utils/chatHelpers";

interface UseChatMessagesParams {
  isLoggedIn: boolean;
  groupId: string | null;
  userId: string;
  userDokter: string;
  conversationID: string | undefined;
  decryptedToken: string;
}

interface UseChatMessagesReturn {
  messages: ParsedMessage[];
  replyTo: ReplyToData | null;
  setReplyTo: (msg: ReplyToData | null) => void;
  loading: boolean;
  hasMore: boolean;
  failedAttachmentCount: number;
  attachmentSyncWarning: string | null;
  handleSend: (message: string) => Promise<void>;
  handleSendMedia: (
    file: File,
    mediaType: MediaKind,
  ) => Promise<void>;
  loadChatHistory: (next: any) => Promise<void>;
  refreshMessages: () => Promise<void>;
  retryFailedAttachments: () => Promise<void>;
}

interface FailedAttachmentSync {
  file: File;
  mediaType: MediaKind;
  messageID: string;
  replyTo: ReplyToData | null;
}

// conversationType 2 = Group di ZIM
const CONVERSATION_TYPE = 2;

/**
 * Manages the full message lifecycle:
 * - Fetching history from ZIM (initial load + missed messages on re-enter)
 * - Listening for incoming messages
 * - Optimistic send for text messages
 * - Sending media via ZIM SDK
 */
export function useChatMessages({
  isLoggedIn,
  groupId,
  userId,
  userDokter,
  conversationID,
  decryptedToken,
}: UseChatMessagesParams): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ParsedMessage[]>([]);
  const [messageIds, setMessageIds] = useState<Set<string>>(new Set());
  const [replyTo, setReplyTo] = useState<ReplyToData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextMessage, setNextMessage] = useState<any>(null);
  const [failedAttachmentSyncs, setFailedAttachmentSyncs] = useState<
    Record<string, FailedAttachmentSync>
  >({});
  const [attachmentSyncWarning, setAttachmentSyncWarning] = useState<string | null>(null);

  // Track whether initial history load has been done for this session
  const initialLoadDoneRef = useRef(false);
  const localPreviewUrlsRef = useRef<Record<string, string>>({});

  // Keep refs in sync for use inside callbacks
  const messagesRef = useRef(messages);
  const messageIdsRef = useRef(messageIds);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    messageIdsRef.current = messageIds;
  }, [messageIds]);

  // ─── Reset saat conversationID berubah ───────────────────────────────────

  useEffect(() => {
    Object.values(localPreviewUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    localPreviewUrlsRef.current = {};
    setMessages([]);
    setMessageIds(new Set());
    setHasMore(true);
    setNextMessage(null);
    setFailedAttachmentSyncs({});
    setAttachmentSyncWarning(null);
    initialLoadDoneRef.current = false;
  }, [conversationID]);

  useEffect(() => {
    return () => {
      Object.values(localPreviewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      localPreviewUrlsRef.current = {};
    };
  }, []);

  // ─── Parse & push incoming messages ──────────────────────────────────────

  const parseAndPushMessages = useCallback(
    (
      messageList: ReceivedMessage[],
      dokterId: string,
      currentUserId: string,
    ) => {
      const newMessages: ParsedMessage[] = [];
      const newIds = new Set(messageIdsRef.current);

      for (const msg of messageList) {
        if (newIds.has(msg.messageID)) continue;
        newIds.add(msg.messageID);

        // Cek pesan end session
        if (typeof msg.message === "string" && msg.message.startsWith("end|")) {
          const parts = msg.message.split("|");
          const endId = parts[1];
          if (Number(endId) === Number(conversationID)) {
            window.dispatchEvent(new CustomEvent("consultation:ended"));
          }
        }

        newMessages.push(buildParsedMessage(msg, dokterId, currentUserId));
      }

      if (newMessages.length === 0) return;

      setMessages((prev) => {
        const existingIds = new Set(prev.map(m => m.messageID));
        let updated = [...prev];

        for (const newMsg of newMessages) {
          if (existingIds.has(newMsg.messageID)) continue;
          if (newMsg.isOwn) {
            // Replace optimistic message jika konten sama
            const optimisticIndex = updated.findIndex(
              (m) =>
                m.messageID.startsWith("local-") &&
                m.message === newMsg.message,
            );
            if (optimisticIndex !== -1) {
              updated[optimisticIndex] = newMsg;
              existingIds.add(newMsg.messageID);
              continue;
            }
          }
          updated.push(newMsg);
          existingIds.add(newMsg.messageID);
        }

        // Sort by timestamp untuk pastikan urutan benar
        updated.sort((a, b) => (a.rawTimestamp ?? 0) - (b.rawTimestamp ?? 0));
        saveMessagesToLocalStorage(updated);
        return updated;
      });
      setMessageIds(prev => {
        const updated = new Set(prev);
        newMessages.forEach(m => updated.add(m.messageID));
        return updated;
      });
    },
    [conversationID],
  );

  // ─── ZIM event listeners ──────────────────────────────────────────────────
  //
  // PENTING: Sengaja TIDAK memanggil zim.off() saat unmount.
  //
  // Ketika zim.off() dipanggil, ZIM SDK menginterpretasikannya sebagai sinyal
  // bahwa user selesai, lalu secara internal memanggil leaveGroup ke server
  // (terlihat di console sebagai [API.leaveGroup] dari index.js:1).
  //
  // Solusi: gunakan ref untuk handler agar selalu up-to-date, daftarkan
  // listener hanya SEKALI dan biarkan hidup selama sesi ZIM aktif.

  const parseAndPushMessagesRef = useRef(parseAndPushMessages);
  const userDokterRef = useRef(userDokter);
  const userIdRef = useRef(userId);

  useEffect(() => {
    parseAndPushMessagesRef.current = parseAndPushMessages;
  }, [parseAndPushMessages]);
  useEffect(() => {
    userDokterRef.current = userDokter;
    userIdRef.current = userId;
  }, [userDokter, userId]);

  const listenersRegisteredRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || !userDokter || listenersRegisteredRef.current) return;
    listenersRegisteredRef.current = true;

    const handleMessage = (
      _zim: any,
      data: { messageList: ReceivedMessage[] },
    ) => {
      console.log("📩 Pesan masuk:", data.messageList);
      // Gunakan ref terbaru — aman meski komponen re-render
      parseAndPushMessagesRef.current(
        data.messageList,
        userDokterRef.current,
        userIdRef.current,
      );
    };

    zim.on("groupMessageReceived", handleMessage);
    zim.on("messageReceived", handleMessage);
    zim.on("receivePeerMessage", handleMessage);

    // ✅ Tidak ada return cleanup / zim.off() di sini
    // Mencegah ZIM SDK trigger internal leaveGroup saat komponen unmount
  }, [isLoggedIn, userDokter]);

  const updateSingleMessage = useCallback(
    (messageID: string, updater: (message: ParsedMessage) => ParsedMessage) => {
      setMessages((prev) => {
        const updated = prev.map((item) =>
          item.messageID === messageID ? updater(item) : item,
        );
        saveMessagesToLocalStorage(updated);
        return updated;
      });
    },
    [],
  );

  const clearLocalPreviewUrl = useCallback((messageID: string) => {
    const previewUrl = localPreviewUrlsRef.current[messageID];
    if (!previewUrl) return;

    URL.revokeObjectURL(previewUrl);
    delete localPreviewUrlsRef.current[messageID];
  }, []);

  const promoteBackendImageUrl = useCallback(
    (messageID: string, backendUrl: string) => {
      const probe = new Image();

      probe.onload = () => {
        updateSingleMessage(messageID, (message) => ({
          ...message,
          imageUrl: backendUrl,
        }));
        clearLocalPreviewUrl(messageID);
      };

      probe.onerror = () => {
        // Keep the local preview as a temporary fallback.
      };

      probe.src = backendUrl;
    },
    [clearLocalPreviewUrl, updateSingleMessage],
  );

  const syncAttachmentToLms = useCallback(
    async (
      file: File,
      mediaType: MediaKind,
      messageID: string,
      replySnapshot: ReplyToData | null,
    ) => {
      if (!groupId) {
        throw new Error("Room chat belum tersedia");
      }

      const mediaTypeMap: Record<MediaKind, number> = {
        image: 11,
        file: 12,
        audio: 13,
        video: 14,
      };

      const formData = new FormData();
      formData.append("roomId", groupId);
      formData.append("to_userId", userDokter);
      formData.append("from_userId", userId);
      formData.append("type_message", String(mediaTypeMap[mediaType]));
      formData.append("message_id", messageID);
      formData.append("file", file);

      if (replySnapshot) {
        formData.append("user_reply_id", replySnapshot.senderUserID);
        formData.append("message_id_reply", replySnapshot.messageID);
        formData.append("type_reply", replySnapshot.type_reply);
        formData.append("value_reply", replySnapshot.message);
      }

      const response = await axios.post("chat/attachment", formData, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data?.data ?? response.data;
    },
    [decryptedToken, groupId, userDokter, userId],
  );

  const applySyncedAttachment = useCallback(
    (messageID: string, backendItem: any, fallbackReplyTo: ReplyToData | null) => {
      const parsed = parseBackendChatMessage(backendItem, userDokter);
      const localPreviewUrl = localPreviewUrlsRef.current[messageID];
      const shouldKeepLocalPreview =
        !!localPreviewUrl &&
        parsed?.mediaKind === "image" &&
        !!parsed?.imageUrl &&
        !parsed.imageUrl.startsWith("blob:");

      updateSingleMessage(messageID, (message) => ({
        ...(parsed || message),
        messageID,
        isOwn: true,
        replyTo: parsed?.replyTo || fallbackReplyTo || message.replyTo,
        message: parsed?.message || message.message,
        imageUrl:
          shouldKeepLocalPreview
            ? message.imageUrl
            : parsed?.imageUrl || message.imageUrl,
        fileName: parsed?.fileName || message.fileName,
        fileSize: parsed?.fileSize || message.fileSize,
        mediaKind: parsed?.mediaKind || message.mediaKind,
        syncStatus: "synced",
        isUploading: false,
        uploadFailed: false,
      }));

      if (shouldKeepLocalPreview && parsed?.imageUrl) {
        promoteBackendImageUrl(messageID, parsed.imageUrl);
        return;
      }

      if (parsed?.imageUrl || parsed?.mediaKind !== "image") {
        clearLocalPreviewUrl(messageID);
      }
    },
    [clearLocalPreviewUrl, promoteBackendImageUrl, updateSingleMessage, userDokter],
  );

  // ─── Load chat history (untuk pagination manual) ──────────────────────────

  const loadChatHistory = useCallback(
    async (next: any) => {
      // Guard: jangan load jika userDokter belum siap
      if (loading || !hasMore || !groupId || !userDokter) return;
      setLoading(true);

      try {
        const history = await zim.queryHistoryMessage(
          groupId,
          CONVERSATION_TYPE,
          {
            nextMessage: next,
            count: 100,
            reverse: false, // newest first, nextMessage = cursor ke pesan lebih lama
          },
        );

        console.log("📜 loadChatHistory (group):", history);

        const fetchedMessages = history.messageList as ReceivedMessage[];
        const newMessages: ParsedMessage[] = [];
        const newIds = new Set(messageIdsRef.current);

        for (const msg of fetchedMessages) {
          if (newIds.has(msg.messageID)) continue;
          newIds.add(msg.messageID);

          const isMedia = msg.type !== 1;
          const imageUrl =
            msg.largeImageDownloadUrl ||
            msg.thumbnailDownloadUrl ||
            msg.fileDownloadUrl;

          const parsedData = !isMedia ? safeJsonParse(msg.message) : null;
          const replyToData =
            parsedData && typeof parsedData === "object"
              ? parseReplyData(parsedData, userDokter)
              : null;

          newMessages.push({
            messageID: msg.messageID,
            message: isMedia ? "" : msg.message,
            timestamp: formatTimestamp(msg.timestamp),
            rawTimestamp: msg.timestamp,
            isDoctor: msg.senderUserID === userDokter,
            imageUrl: isMedia ? imageUrl : undefined,
            fileName: isMedia ? msg.fileName : undefined,
            fileSize: isMedia ? (msg as any).fileSize : undefined,
            type: isMedia ? "media" : "text",
            mediaKind: isMedia
              ? resolveMediaKind(msg.type, msg.fileName, imageUrl)
              : undefined,
            replyTo: replyToData,
            syncStatus: isMedia ? "synced" : undefined,
          });
        }

        if (newMessages.length > 0) {
          setMessages((prev) => {
            // Prepend older messages, sort untuk pastikan urutan
            const merged = [...newMessages, ...prev];
            merged.sort(
              (a, b) => (a.rawTimestamp ?? 0) - (b.rawTimestamp ?? 0),
            );
            saveMessagesToLocalStorage(merged);
            return merged;
          });
          setMessageIds(prev => {
            const updated = new Set(prev);
            newMessages.forEach(m => updated.add(m.messageID));
            return updated;
          });
        }

        setNextMessage(history.nextMessage);
        setHasMore(fetchedMessages.length > 0);
      } catch (err) {
        console.error("Failed to load chat history:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupId, userDokter, loading, hasMore],
  );

  // ─── Helper: parse satu raw ZIM message ke ParsedMessage ────────────────

  const parseRawMessage = useCallback(
    (msg: ReceivedMessage): ParsedMessage => {
      const isMedia = msg.type !== 1;
      const imageUrl =
        msg.largeImageDownloadUrl ||
        msg.thumbnailDownloadUrl ||
        msg.fileDownloadUrl;
      const parsedData = !isMedia ? safeJsonParse(msg.message) : null;
      const replyToData =
        parsedData && typeof parsedData === "object"
          ? parseReplyData(parsedData, userDokter)
          : null;

      return {
        messageID: msg.messageID,
        message: isMedia ? "" : msg.message,
        timestamp: formatTimestamp(msg.timestamp),
        rawTimestamp: msg.timestamp,
        isDoctor: msg.senderUserID === userDokter,
        imageUrl: isMedia ? imageUrl : undefined,
        fileName: isMedia ? msg.fileName : undefined,
        fileSize: isMedia ? (msg as any).fileSize : undefined,
        type: isMedia ? "media" : "text",
        mediaKind: isMedia
          ? resolveMediaKind(msg.type, msg.fileName, imageUrl)
          : undefined,
        replyTo: replyToData,
        syncStatus: isMedia ? "synced" : undefined,
      };
    },
    [userDokter],
  );

  // ─── Load semua pesan saat pertama kali masuk / re-enter halaman chat ────
  //
  // PENTING tentang reverse:
  //   reverse: false (default) → ZIM mengembalikan pesan dari TERBARU ke TERLAMA
  //                               nextMessage menunjuk ke pesan lebih lama lagi
  //   reverse: true            → ZIM mengembalikan pesan dari TERLAMA ke TERBARU
  //                               jika ada 500 pesan & count=100, hanya dapat 100 pesan pertama
  //
  // Strategi yang benar:
  //   1. Fetch tanpa reverse (newest first), count=100 per batch
  //   2. Loop terus pakai nextMessage sampai tidak ada lagi
  //   3. Setelah semua terkumpul, sort ascending untuk tampilan chat

  // ─── Fetch history dari backend (sumber kebenaran utama) ─────────────────
  //
  // ZIM queryHistoryMessage hanya mengembalikan pesan yang dikirim SETELAH
  // user bergabung ke group. Pesan yang dikirim dokter sebelum pasien join
  // tidak akan muncul dari ZIM. Backend menyimpan semua pesan sejak awal,
  // sehingga backend menjadi sumber utama history yang lengkap.

  const fetchBackendHistory = useCallback(async (): Promise<
    ParsedMessage[]
  > => {
    console.log(
      "🔄 Fetching backend history for conversationID:",
      conversationID,
    );
    if (!conversationID || !decryptedToken) return [];

    try {
      const response = await axios.get(`chat/${conversationID}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const rawList: any[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.data?.List)
            ? response.data.data.List
          : Array.isArray(response.data?.messages)
            ? response.data.messages
            : [];

      console.log(`📡 Backend history: ${rawList.length} pesan`);

      const parsed: ParsedMessage[] = [];

      for (const item of rawList) {
        const source = item?.List || item;
        const parsedMessage = parseBackendChatMessage(source, userDokter);
        if (!parsedMessage) continue;
        const rawMessage = parsedMessage.message;

        // Deteksi end session dari backend history juga
        if (typeof rawMessage === "string" && rawMessage.startsWith("end|")) {
          const endId = rawMessage.split("|")[1];
          if (Number(endId) === Number(conversationID)) {
            window.dispatchEvent(new CustomEvent("consultation:ended"));
          }
        }

        parsed.push(parsedMessage);
      }

      return parsed;
    } catch (err) {
      console.warn("⚠️ Gagal fetch backend history, fallback ke ZIM:", err);
      return [];
    }
  }, [conversationID, decryptedToken, userDokter]);

  const loadAllMessages = useCallback(async () => {
    if (!groupId || !userDokter || !isLoggedIn) return;

    console.log("🔄 Loading all messages on enter chat page...");
    setLoading(true);

    try {
      // ── 1. Fetch dari backend (semua pesan sejak awal konsultasi) ──────────
      const backendMessages = await fetchBackendHistory();
      const allIds = new Set<string>(backendMessages.map((m) => m.messageID));

      // ── 2. Fetch dari ZIM (untuk merge, terutama media & pesan real-time) ──
      const zimMessages: ParsedMessage[] = [];
      let cursor: any = undefined;
      let batchCount = 0;
      const MAX_BATCHES = 20;

      while (batchCount < MAX_BATCHES) {
        const history = await zim.queryHistoryMessage(
          groupId,
          CONVERSATION_TYPE,
          {
            nextMessage: cursor,
            count: 100,
            reverse: false, // newest first
          },
        );

        const batch = history.messageList as ReceivedMessage[];
        console.log(`📜 ZIM Batch ${batchCount + 1}: ${batch.length} pesan`);

        if (batch.length === 0) break;

        for (const msg of batch) {
          if (allIds.has(msg.messageID)) continue; // skip kalau sudah ada dari backend
          allIds.add(msg.messageID);

          if (
            typeof msg.message === "string" &&
            msg.message.startsWith("end|")
          ) {
            const endId = msg.message.split("|")[1];
            if (Number(endId) === Number(conversationID)) {
              window.dispatchEvent(new CustomEvent("consultation:ended"));
            }
          }

          zimMessages.push(parseRawMessage(msg));
        }

        batchCount++;
        if (batch.length < 100 || !history.nextMessage) break;
        cursor = history.nextMessage;
      }

      // ── 3. Merge backend + ZIM, sort ascending ─────────────────────────────
      const allMessages = [...backendMessages, ...zimMessages];
      allMessages.sort((a, b) => (a.rawTimestamp ?? 0) - (b.rawTimestamp ?? 0));

      console.log(
        `✅ Total pesan: ${allMessages.length} (backend: ${backendMessages.length}, ZIM tambahan: ${zimMessages.length})`,
      );

      setMessages(allMessages);
      setMessageIds(prev => {
        const updated = new Set(prev);
        allIds.forEach(id => updated.add(id));
        return updated;
      });
      setHasMore(false);
      saveMessagesToLocalStorage(allMessages);

      initialLoadDoneRef.current = true;
    } catch (err) {
      console.error("❌ Failed to load all messages:", err);
    } finally {
      setLoading(false);
    }
  }, [
    groupId,
    userDokter,
    isLoggedIn,
    conversationID,
    parseRawMessage,
    fetchBackendHistory,
  ]);

  const refreshMessages = useCallback(async () => {
    initialLoadDoneRef.current = false; // reset flag
    setMessages([]);
    setMessageIds(new Set());
    await loadAllMessages();
  }, [loadAllMessages]);

  // Trigger load semua pesan saat pertama kali siap
  useEffect(() => {
    if (isLoggedIn && groupId && userDokter && !initialLoadDoneRef.current) {
      loadAllMessages();
    }
  }, [isLoggedIn, groupId, userDokter, loadAllMessages]);

  // ─── Send text ────────────────────────────────────────────────────────────

  const handleSend = async (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const payloadData: any = {
      message: trimmedMessage,
      type: "text",
      time: Date.now(),
    };

    if (replyTo) {
      let replyMessageContent = replyTo.message;
      if (typeof replyTo.message === "string") {
        const parsed = safeJsonParse(replyTo.message);
        if (parsed) replyMessageContent = parsed.message || replyTo.message;
      }
      payloadData.user_reply_id =
        replyTo.senderUserID || (replyTo.isDoctor ? userDokter : userId);
      payloadData.message_id_reply = replyTo.messageID;
      payloadData.type_reply = replyTo.type_reply || "text";
      payloadData.value_reply = replyMessageContent;
    }

    const messageString = JSON.stringify(payloadData);

    // Optimistic update
    const optimisticId = `local-${Date.now()}`;
    const now = Date.now();
    const optimisticMsg: ParsedMessage = {
      messageID: optimisticId,
      message: messageString,
      type: "text",
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      rawTimestamp: now,
      isDoctor: false,
      replyTo: replyTo,
    };

    setMessages((prev) => {
      const updated = [...prev, optimisticMsg];
      saveMessagesToLocalStorage(updated);
      return updated;
    });
    setMessageIds((prev) => new Set([...prev, optimisticId]));
    setReplyTo(null);

    try {
      await axios.post(
        "chat",
        {
          to_userId: userDokter,
          from_userId: userId,
          message: messageString,
          type_message: 1,
          roomId: groupId,
        },
        { headers: { Authorization: `Bearer ${decryptedToken}` } },
      );
    } catch (error: any) {
      console.error(
        "Failed to send message:",
        error?.response?.data || error.message,
      );
      // Rollback optimistic message
      setMessages((prev) => prev.filter((m) => m.messageID !== optimisticId));
      setMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(optimisticId);
        return next;
      });
    }
  };

  // ─── Send media ───────────────────────────────────────────────────────────

  const handleSendMedia = async (
    file: File,
    mediaType: MediaKind,
  ) => {
    if (!groupId) return;

    const mediaMessageMap: Record<string, any> = {
      image: { fileLocalPath: file, type: 11 },
      file: { fileLocalPath: file, type: 12 },
      audio: { fileLocalPath: file, type: 13, audioDuration: 100 },
      video: { fileLocalPath: file, type: 14, videoDuration: 100 },
    };

    const mediaMessageObj = mediaMessageMap[mediaType];
    if (!mediaMessageObj) return;

    // ── Optimistic: tampilkan preview media + indikator loading ──────────────
    const optimisticId = `local-media-${Date.now()}`;
    const now = Date.now();
    const previewUrl =
      mediaType === "image" || mediaType === "video"
        ? URL.createObjectURL(file)
        : undefined;

    const optimisticMsg: ParsedMessage = {
      messageID: optimisticId,
      message: "[media]",
      type: "media",
      timestamp: new Date(now).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      rawTimestamp: now,
      isDoctor: false,
      imageUrl: previewUrl,
      fileName: file.name,
      fileSize: file.size,
      replyTo: null,
      isUploading: true,
    };

    setMessages((prev) => {
      const updated = [...prev, optimisticMsg];
      return updated;
    });
    setMessageIds((prev) => new Set([...prev, optimisticId]));

    try {
      const replySnapshot = replyTo;
      const res = await zim.sendMessage(
        mediaMessageObj,
        groupId,
        CONVERSATION_TYPE,
        { priority: 1 },
      );

      const msg = res.message;
      if (previewUrl) {
        localPreviewUrlsRef.current[msg.messageID] = previewUrl;
      }
      const newMsg: ParsedMessage = {
        messageID: msg.messageID,
        message: "[media]",
        type: "media",
        timestamp: formatTimestamp(msg.timestamp),
        rawTimestamp: msg.timestamp,
        isDoctor: false,
        imageUrl:
          msg.largeImageDownloadUrl ||
          msg.fileDownloadUrl ||
          msg.thumbnailDownloadUrl ||
          previewUrl,
        fileName: msg.fileName || file.name,
        fileSize: (msg as any).fileSize,
        isUploading: false,
        replyTo: replySnapshot,
        mediaKind: mediaType,
        syncStatus: "pending",
      };

      setMessages((prev) => {
        const updated = prev.map((m) =>
          m.messageID === optimisticId ? newMsg : m,
        );
        saveMessagesToLocalStorage(updated);
        return updated;
      });
      setMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(optimisticId);
        next.add(msg.messageID);
        return next;
      });
      setReplyTo(null);

      try {
        const syncedMessage = await syncAttachmentToLms(
          file,
          mediaType,
          msg.messageID,
          replySnapshot,
        );

        applySyncedAttachment(msg.messageID, syncedMessage, replySnapshot);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[msg.messageID];
          return updated;
        });
        setAttachmentSyncWarning(null);
      } catch (syncError: any) {
        console.error(
          "Failed to sync media attachment to LMS:",
          syncError?.response?.data || syncError?.message || syncError,
        );
        setFailedAttachmentSyncs((prev) => ({
          ...prev,
          [msg.messageID]: {
            file,
            mediaType,
            messageID: msg.messageID,
            replyTo: replySnapshot,
          },
        }));
        updateSingleMessage(msg.messageID, (message) => ({
          ...message,
          syncStatus: "failed",
        }));
        setAttachmentSyncWarning(
          "Ada lampiran yang sudah terkirim ke chat, tetapi belum tersimpan permanen di LMS.",
        );
      }
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      console.error("Failed to send media message:", error);
      setMessages((prev) =>
        prev.map((m) =>
          m.messageID === optimisticId
            ? { ...m, isUploading: false, uploadFailed: true }
            : m,
        ),
      );
    }
  };

  const retryFailedAttachments = useCallback(async () => {
    const pendingRetries = Object.values(failedAttachmentSyncs);
    if (pendingRetries.length === 0) return;

    let hasFailure = false;

    for (const item of pendingRetries) {
      updateSingleMessage(item.messageID, (message) => ({
        ...message,
        syncStatus: "pending",
      }));

      try {
        const syncedMessage = await syncAttachmentToLms(
          item.file,
          item.mediaType,
          item.messageID,
          item.replyTo,
        );
        applySyncedAttachment(item.messageID, syncedMessage, item.replyTo);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[item.messageID];
          return updated;
        });
      } catch (error: any) {
        hasFailure = true;
        updateSingleMessage(item.messageID, (message) => ({
          ...message,
          syncStatus: "failed",
        }));
        console.error(
          "Retry attachment sync failed:",
          error?.response?.data || error?.message || error,
        );
      }
    }

    setAttachmentSyncWarning(
      hasFailure
        ? "Sebagian lampiran masih belum tersimpan di LMS. Coba sync ulang lagi."
        : null,
    );
  }, [
    applySyncedAttachment,
    failedAttachmentSyncs,
    syncAttachmentToLms,
    updateSingleMessage,
  ]);

  return {
    messages,
    replyTo,
    setReplyTo,
    loading,
    hasMore,
    failedAttachmentCount: Object.keys(failedAttachmentSyncs).length,
    attachmentSyncWarning,
    handleSend,
    handleSendMedia,
    loadChatHistory,
    refreshMessages,
    retryFailedAttachments,
  };
}
